import React from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { AssistanceExecutionDTO } from "../../models/AssistanceExecution/AssistanceExecutionDTO";
import { FormattedMessage } from "react-intl";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

type Location = {
  key: string;
  lat: number;
  lon: number;
  label: string;
};

type ExecutionMapModalProps = {
  isOpen: boolean;
  onClose: () => void;
  executions: AssistanceExecutionDTO[];
};

// Função que desloca ligeiramente coordenadas repetidas para evitar sobreposição premitindo visualização clara no mapa
function getOffsetLocation(
  lat: number,
  lon: number,
  count: number
): [number, number] {
  const offset = 0.0001; // deslocamento em graus    +- 11 metros
  const angle = (count * 45 * Math.PI) / 180; // distribui circularmente
  const newLat = lat + Math.sin(angle) * offset;
  const newLon = lon + Math.cos(angle) * offset;
  return [newLat, newLon];
}

export default function ExecutionMapModal({
  isOpen,
  onClose,
  executions,
}: ExecutionMapModalProps) {
  const coordinateMap = new Map<string, number>();

  const locations: Location[] = executions.flatMap((exec) => {
    const locs: Location[] = [];

    const processLocation = (
      type: "start" | "end",
      lat: number | null | undefined,
      lon: number | null | undefined,
      label: string,
      keySuffix: string
    ) => {
      if (typeof lat === "number" && typeof lon === "number") {
        const coordKey = `${lat},${lon}`;
        const count = coordinateMap.get(coordKey) ?? 0;
        coordinateMap.set(coordKey, count + 1);

        const [offsetLat, offsetLon] = getOffsetLocation(lat, lon, count);

        locs.push({
          key: `${type}-${keySuffix}`,
          lat: offsetLat,
          lon: offsetLon,
          label,
        });
      }
    };

    processLocation(
      "start",
      exec.startLocationLat,
      exec.startLocationLon,
      `Início: ${exec.technicianName} (${exec.executionStart})`,
      String(exec.id)
    );

    processLocation(
      "end",
      exec.endLocationLat,
      exec.endLocationLon,
      `Fim: ${exec.technicianName} (${exec.executionEnd})`,
      String(exec.id)
    );

    return locs;
  });

  const centerLat =
    locations.length > 0
      ? locations.reduce((sum, loc) => sum + loc.lat, 0) / locations.length
      : 0;
  const centerLon =
    locations.length > 0
      ? locations.reduce((sum, loc) => sum + loc.lon, 0) / locations.length
      : 0;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      style={{ maxWidth: "900px", width: "90%" }}
    >
      <ModalHeader toggle={onClose}>
        <FormattedMessage id="Screens.AssistanceExecutions.Map" />
      </ModalHeader>
      <ModalBody style={{ height: "500px", padding: 0 }}>
        {locations.length === 0 ? (
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <FormattedMessage id="Screens.AssistanceExecutions.MapNoLocations" />
          </div>
        ) : (
          <MapContainer
            center={[centerLat, centerLon]}
            zoom={13}
            style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {locations.map((loc) => (
              <Marker key={loc.key} position={[loc.lat, loc.lon]}>
                <Popup>{loc.label}</Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </ModalBody>
    </Modal>
  );
}
